import { z } from "zod";
import {
  InterviewLanguage
} from "../constants/languages.js";
import { splitProjectQuestions } from "../helpers/utilities.js";
import {
  stringifyMultilineContent,
  stringifyVariableAsPlaceholder
} from "./../content/plaintext.js";
import { mapJobTypeDbToJobType } from "./jobType.js";
import { mapComplexQuestionDbToComplexQuestion } from "./question.js";
const ProjectEmailDigestFrequency = {
  INSTANT: "instant",
  DAILY: "daily"
};
const InterviewAgentType = {
  GenericInterviewer: "generic",
  RecruitingInterviewer: "recruiter",
  Training: "training"
};
const ResumeCollectionType = {
  BeforeInterview: "before_interview",
  AfterInterview: "after_interview"
};
const allResumeCollectionTypes = [
  ResumeCollectionType.BeforeInterview,
  ResumeCollectionType.AfterInterview
];
const resumeCollectionTypeSchema = z.enum(allResumeCollectionTypes);
const ProjectCallProvider = {
  /** Use LiveKit voice agent for all interviews where the language is (technically) supported by the LiveKit voice agent (voice model quality may vary depending on language). */
  Livekit: "livekit-py",
  /** Use default. Usually Retell, but it depends interview language. See checkShouldUseLivekit(). */
  Default: null
};
const projectLinkAccesses = ["default", "existing-only"];
const projectLinkAccessSchema = z.enum(projectLinkAccesses);
const JobTypeProperty = {
  AssessmentId: "assessment_id",
  Questions: "questions",
  PromptContext: "prompt_context",
  JobDescription: "job_description",
  Language: "language",
  MultilangLanguages: "multilang_languages",
  LanguageVoice: "language_voice",
  AutoAdvanceAssessmentScoreThreshold: "auto_advance_assessment_score_threshold",
  ShouldForceFullscreen: "should_force_fullscreen",
  ShouldRecordVideo: "should_record_video",
  ResumeCollectionType: "resume_collection_type",
  WelcomeMessage: "welcome_message",
  OutroMessage: "outro_message",
  RedirectUrl: "redirect_url",
  InterviewRetakeSettings: "interview_retake_settings"
};
const SpecialProjectCustomFieldName = {
  AdvancedNextSteps: "advanced_next_steps"
};
function getProjectAndJobTypeProperty(project, propertyName) {
  if (project.job_type) {
    const jobType = project.job_type;
    switch (propertyName) {
      case JobTypeProperty.Questions:
        return jobType.questions;
      case JobTypeProperty.PromptContext:
        return jobType.prompt_context ? stringifyMultilineContent(jobType.prompt_context, {
          stringifyVariable: stringifyVariableAsPlaceholder
        }) : null;
      case JobTypeProperty.JobDescription:
        return stringifyMultilineContent(jobType.job_description, {
          stringifyVariable: stringifyVariableAsPlaceholder
        });
      case JobTypeProperty.Language:
        return jobType.settings.interview_languages[0];
      case JobTypeProperty.MultilangLanguages:
        return jobType.settings.multilang_languages;
      case JobTypeProperty.LanguageVoice:
        return jobType.settings.language_voice;
      case JobTypeProperty.AutoAdvanceAssessmentScoreThreshold:
        return jobType.settings.auto_advance_assessment_score_threshold;
      case JobTypeProperty.ShouldForceFullscreen:
        return jobType.settings.should_force_fullscreen;
      case JobTypeProperty.ShouldRecordVideo:
        return jobType.settings.should_record_video;
      case JobTypeProperty.ResumeCollectionType:
        return jobType.settings.resume_collection_type;
      case JobTypeProperty.AssessmentId:
        return jobType.assessment_id;
      case JobTypeProperty.WelcomeMessage:
        return jobType.settings.welcome_message;
      case JobTypeProperty.OutroMessage:
        return jobType.settings.outro_message;
      case JobTypeProperty.RedirectUrl:
        return jobType.settings.redirect_url;
      default:
        throw new Error(`Invalid property name`);
    }
  }
  switch (propertyName) {
    case JobTypeProperty.Questions:
      return splitProjectQuestions(project.questions).basicQuestions;
    case JobTypeProperty.PromptContext:
      return project.settings.prompt_context;
    case JobTypeProperty.JobDescription:
      return project.job_description;
    case JobTypeProperty.Language:
      return project.settings.interview_language;
    case JobTypeProperty.MultilangLanguages:
      return project.settings.multilang_languages;
    case JobTypeProperty.LanguageVoice:
      return project.settings.language_voice;
    case JobTypeProperty.AutoAdvanceAssessmentScoreThreshold:
      return project.settings.auto_advance_assessment_score_threshold;
    case JobTypeProperty.ShouldForceFullscreen:
      return project.settings.should_force_fullscreen;
    case JobTypeProperty.ShouldRecordVideo:
      return project.settings.should_record_video;
    case JobTypeProperty.ResumeCollectionType:
      return project.settings.resume_collection_type;
    case JobTypeProperty.AssessmentId:
      return project.assessment_id;
    case JobTypeProperty.WelcomeMessage:
      return project.settings.welcome_message;
    case JobTypeProperty.OutroMessage:
      return project.settings.outro_message;
    case JobTypeProperty.RedirectUrl:
      return project.settings.redirect_url;
    default:
      throw new Error(`Invalid property name`);
  }
}
function isValidJobOpeningLocation(location) {
  const loc = location;
  return Boolean(
    location && typeof location === "object" && loc.description && typeof loc.description === "string"
  );
}
function isValidJobOpeningPay(pay) {
  const p = pay;
  return Boolean(
    pay && typeof pay === "object" && p.description && typeof p.description === "string"
  );
}
function isValidJobOpeningHours(hours) {
  const h = hours;
  return Boolean(
    hours && typeof hours === "object" && h.description && typeof h.description === "string"
  );
}
function getInterviewLinkForProject(interviewAppBaseUrl, company, project) {
  if (company.settings.whitelabel_domain)
    return `${company.settings.whitelabel_domain}/i/${project.short_id}`;
  return `${interviewAppBaseUrl}/${project.short_id}`;
}
const ProjectSchedulingMode = {
  CollectAvailability: "collect_availability",
  CollectAvailabilityAndSchedule: "collect_availability_and_schedule"
};
const ProjectMode = {
  SURVEY: "survey",
  VOICE_AGENT: "voice_agent"
};
const allProjectModes = [
  ProjectMode.SURVEY,
  ProjectMode.VOICE_AGENT
];
const projectModeSchema = z.enum(allProjectModes);
function checkIsProjectWithJobType(project) {
  return !!project.job_type;
}
const projectSettingsDefaults = {
  send_email_digest_to_all_teammates: true,
  send_email_digest_to_project_owners: true,
  email_digest_recipients: [],
  email_digest_frequency: ProjectEmailDigestFrequency.DAILY,
  max_interview_count: null,
  is_slack_enabled: true,
  slack_channel_id: null,
  prompt_context: null,
  max_clarifying_questions_count: 2,
  redirect_url: null,
  welcome_message: null,
  outro_message: null,
  interview_agent_type: InterviewAgentType.GenericInterviewer,
  call_provider: ProjectCallProvider.Default,
  is_incentive_charity_only: false,
  resume_collection_type: null,
  interview_language: InterviewLanguage.ENGLISH,
  multilang_languages: [],
  language_voice: null,
  should_force_fullscreen: false,
  should_record_video: false,
  acceptance_template_id: null,
  rejection_template_id: null,
  outreach_template_id: null,
  auto_advance_assessment_score_threshold: null,
  link_access: "default",
  scheduling_configuration_id: null,
  scheduling_mode: null,
  form_id: null,
  is_participant_facing_feedback_enabled: true
};
function mapToProject(projectWithEntities) {
  const {
    complex_questions: complexQuestions,
    job_type: jobTypeDb,
    members,
    ...projectDb
  } = projectWithEntities;
  const questions = projectDb.questions.map((q) => {
    if ("question_id" in q) {
      const matchingComplexQuestion = complexQuestions.find(
        (cq) => cq.id === q.question_id
      );
      if (!matchingComplexQuestion) {
        throw new Error(`Complex question with id ${q.question_id} not found`);
      }
      return mapComplexQuestionDbToComplexQuestion(matchingComplexQuestion);
    }
    return q;
  });
  const owner_teammate_ids = members.map((member) => member.teammate_id);
  return {
    ...projectDb,
    questions,
    owner_teammate_ids,
    settings: mapProjectSettingsDbToProjectSettings(projectDb.settings),
    mode: projectDb.mode || ProjectMode.SURVEY,
    job_type: jobTypeDb ? mapJobTypeDbToJobType(jobTypeDb) : null
  };
}
const mapProjectSettingsDbToProjectSettings = (projectSettingsDb) => {
  const settings = {
    ...projectSettingsDefaults,
    ...typeof projectSettingsDb === "object" ? projectSettingsDb : {}
  };
  return settings;
};
export {
  InterviewAgentType,
  JobTypeProperty,
  ProjectCallProvider,
  ProjectEmailDigestFrequency,
  ProjectMode,
  ProjectSchedulingMode,
  ResumeCollectionType,
  SpecialProjectCustomFieldName,
  allProjectModes,
  allResumeCollectionTypes,
  checkIsProjectWithJobType,
  getInterviewLinkForProject,
  getProjectAndJobTypeProperty,
  isValidJobOpeningHours,
  isValidJobOpeningLocation,
  isValidJobOpeningPay,
  mapProjectSettingsDbToProjectSettings,
  mapToProject,
  projectLinkAccessSchema,
  projectModeSchema,
  projectSettingsDefaults,
  resumeCollectionTypeSchema
};
//# sourceMappingURL=project.js.map
